/**
 * Page Builder element normalizer ("converter").
 *
 * Old pages were saved with the legacy element shape (e.g. a border kept as the
 * separate keys `borderNumber` + `borderColor`, no hover/typography keys, etc.).
 * The new heygov-style choosers read/write a richer shape (single `style.border`
 * string, hover sibling keys, opacity, …). When an old page is loaded into the
 * builder we run it through this normalizer so every new chooser has correctly
 * shaped data to bind to — without changing how the page looks.
 *
 * Strategy (see plan): lazy, in-memory, on load. The stored pb_json stays
 * untouched until the user saves again, at which point the normalized shape is
 * persisted. The publish engine reads BOTH shapes during the transition.
 *
 * Two layers:
 *   1. backfillDefaults() — additively copy any MISSING key from the element's
 *      elements.json default (visually neutral; never overwrites existing data).
 *      This covers every newly-added style/prop key automatically.
 *   2. TRANSFORMS[type] — the genuine RESHAPES that a merge can't express
 *      (legacy border composition, String→Number counts, …).
 *
 * Idempotent: each element is stamped with `_v === PB_SCHEMA` and skipped next
 * time. Bump PB_SCHEMA whenever a new transform is added.
 */

import elementsData from "@/views/page-builder/elements";

export const PB_SCHEMA = 3;

// type -> default element node (from elements.json)
const DEFAULTS = {};
for (const group of elementsData || []) {
    for (const el of group.elements || []) {
        DEFAULTS[el.type] = el;
    }
}

function isPlainObject(v) {
    return v !== null && typeof v === "object" && !Array.isArray(v);
}

// Keys we must never copy from a default onto an existing element.
// Slider caption bg/opacity/width are intentionally NOT backfilled: legacy sliders must keep
// their original look (PHP default dark scrim) — only NEW sliders get the opacity:0/black/auto
// defaults from elements.json. Backfilling would retro-change existing sliders.
const SKIP_BACKFILL_KEYS = new Set(["id", "items", "value", "text", "heading", "label", "icon", "image", "captionBackgroundColor", "captionOpacity", "captionWidth"]);

/**
 * Copy keys present in `defaults` but missing on `target`. Existing keys (even
 * empty strings) are left as-is so the element keeps its current appearance.
 */
function backfillDefaults(target, defaults) {
    if (!isPlainObject(defaults) || !isPlainObject(target)) return;
    for (const key of Object.keys(defaults)) {
        if (SKIP_BACKFILL_KEYS.has(key)) continue;
        const dVal = defaults[key];
        if (isPlainObject(dVal)) {
            if (!isPlainObject(target[key])) target[key] = {};
            backfillDefaults(target[key], dVal);
        } else if (!(key in target) || target[key] === undefined) {
            target[key] = Array.isArray(dVal) ? JSON.parse(JSON.stringify(dVal)) : dVal;
        }
    }
}

/**
 * Compose a legacy split border into the new single `style.border` string.
 * Visually neutral: only runs when an actual border width was set, and reuses
 * the stored style if present (old Button always rendered "solid").
 */
function composeLegacyBorder(style, { widthKey, styleKey, colorKey, widthIsNumber, targetKey = "border" }) {
    if (!isPlainObject(style)) return;
    // Already in new shape — leave it.
    if (typeof style[targetKey] === "string" && style[targetKey].trim() !== "") return;

    const rawWidth = style[widthKey];
    const hasWidth = rawWidth !== undefined && rawWidth !== null && rawWidth !== "" && rawWidth !== 0 && rawWidth !== "0";
    if (!hasWidth) return; // no border before → keep neutral (empty)

    const width = widthIsNumber ? `${rawWidth}px` : rawWidth;
    const borderStyle = style[styleKey] || "solid";
    const color = style[colorKey] || "currentColor";
    style[targetKey] = `${width} ${borderStyle} ${color}`;
}

function toNumber(value, fallback) {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : fallback;
}

/** Per-type reshapes that a plain default-merge cannot express. */
const TRANSFORMS = {
    // Column: legacy split border (borderWidth/borderStyle/borderColor) → single style.border,
    // then drop the legacy keys so they can't override once the user edits the new control.
    Column(el) {
        composeLegacyBorder(el.style, { widthKey: "borderWidth", styleKey: "borderStyle", colorKey: "borderColor", widthIsNumber: false });
        if (isPlainObject(el.style)) {
            delete el.style.borderWidth;
            delete el.style.borderStyle;
            delete el.style.borderColor;
        }
    },
    Button(el) {
        composeLegacyBorder(el.style, { widthKey: "borderNumber", styleKey: "borderStyle", colorKey: "borderColor", widthIsNumber: true });
    },
    BusinessDirectory(el) {
        if (!isPlainObject(el.props)) return;
        if (el.props.initialCount != null) el.props.initialCount = toNumber(el.props.initialCount, 6);
        if (el.props.loadMoreStep != null) el.props.loadMoreStep = toNumber(el.props.loadMoreStep, 6);
    },
};

/**
 * Normalize a single element node in place (and recurse into column children).
 * Returns the same node for convenience.
 */
export function normalizeElement(el) {
    if (!isPlainObject(el)) return el;
    if (el._v === PB_SCHEMA) return el;

    if (!isPlainObject(el.props)) el.props = {};
    if (!isPlainObject(el.style)) el.style = {};

    const def = DEFAULTS[el.type];
    if (def) backfillDefaults(el, def);

    const transform = TRANSFORMS[el.type];
    if (transform) transform(el);

    if (Array.isArray(el.childs)) el.childs.forEach(normalizeElement);

    el._v = PB_SCHEMA;
    return el;
}

/**
 * Normalize a full pageLayouts array (columns/layouts with `.childs`).
 * Safe to call on already-normalized data (idempotent).
 */
export function normalizePage(layouts) {
    if (Array.isArray(layouts)) layouts.forEach(normalizeElement);
    return layouts;
}

export default normalizePage;
